import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { ITheme } from "@/api/AppApi/services/company/interfaces/ICompany";
import { hexToRGBA } from "./functions";
import { isServer } from "@tanstack/react-query";
import { IProduct_Category_Config_Customize } from "@/api/AppApi/services/home/interfaces/ICategory";

const figmaDefaultColors = {
  primaryColor: "#0d76d7",
  primaryInnerColor: "#ffffff",
  secondaryColor: "#edb614",
  secondaryInnerColor: "#ffffff",
  floatingColor: "#eb5757",
  floatingInnerColor: "#ffffff",
  newBadgeBgColor: "#f9a000",
  saleBadgeBgColor: "#ed1414",
};
const figmaColors = {
  dark: "#2b2b2b",
  neuturals: "#484860",
  venyDolsyBlack: "#1F1F1F",
  white: "#ffffff",
  green: "#27ae60",
  yellow: "#f4bf23",
  red: "#eb5757",
  //Gray
  gray1: "#5b5b5b",
  gray2: "#acacac",
  gray3: "#c4c4c4",
  gray4: "#e2e2e2",
  gray5: "#f2f2f2",
  gray6: "#fafafa",
  venyDolsyGray: "#cac3b6",
  //Status
  success: "#4caf50",
  processing: "#f08e33",
  cancelled: "#d62929",
  //Rating
  excellent: "#4caf50",
  good: "#6599ff",
  averrage: "#f1b505",
  poor: "#fbed1f",
  bad: "#ed563e",
};

const propertyValue = (value: string | null, defaultValue = "") =>
  value && value.length > 0 ? value : defaultValue;

export const setProperty = (
  name: string,
  value: string | null,
  defaultValue = "",
) => {
  if (isServer) return;
  const val = propertyValue(value, defaultValue);
  // const r = document.querySelector(":root") as HTMLElement;
  // r.style.setProperty(name, val);
  document?.documentElement.style.setProperty(name, val);
};

// const setCustomFonts = (
//   language: string,
//   en_font_family: string,
//   ar_font_family: string
// ) => {
//   // if (document.querySelector("#customfontrel")) return;
//   const enFont = propertyValue(en_font_family, "Poppins");
//   const arFont = propertyValue(ar_font_family, "Almarai");
//   let fontLink = `https://fonts.googleapis.com/css2?family=${enFont.replace(
//     /\s/g,
//     "+"
//   )}:wght@400;500;600;700`;
//   if (arFont !== enFont)
//     fontLink += `&family=${arFont.replace(/\s/g, "+")}:wght@400;500;600;700`;
//   fontLink += `&display=swap`;
//   let link = document.createElement("link");
//   link.setAttribute("id", "customfontrel");
//   link.setAttribute("rel", "stylesheet");
//   link.setAttribute("type", "text/css");
//   link.setAttribute("href", fontLink);
//   document.head.appendChild(link);

//   setProperty("--ar-font", `${arFont},sans-serif`);
//   setProperty("--en-font", `${enFont},sans-serif`);
//   setProperty(
//     "--font-family",
//     language === "ar"
//       ? `${enFont},${arFont},sans-serif`
//       : `${enFont},${arFont},sans-serif`
//   );
// };

const getColors = (theme: ITheme) => {
  const { colors, components } = theme ?? {};
  const { product, header } = components ?? {};
  const primaryColor = propertyValue(
    colors?.primary_color,
    figmaDefaultColors.primaryColor,
  );
  const primaryInner = propertyValue(
    colors?.primary_inner_color,
    figmaDefaultColors.primaryInnerColor,
  );
  const secondaryColor = propertyValue(
    colors?.secondary_color,
    figmaDefaultColors.secondaryColor,
  );
  const secondaryInner = propertyValue(
    colors?.secondary_inner_color,
    figmaDefaultColors.secondaryInnerColor,
  );
  const floating = propertyValue(
    colors?.floating_color,
    figmaDefaultColors.floatingColor,
  );
  const floatingInner = propertyValue(
    colors?.floating_inner_color,
    figmaDefaultColors.floatingInnerColor,
  );
  const newBadgeColor = propertyValue(
    colors?.new_badge_color,
    figmaColors.white,
  );
  const newBadgeBgColor = propertyValue(
    colors?.new_badge_bg_color,
    figmaDefaultColors.newBadgeBgColor,
  );
  const saleBadgeColor = propertyValue(
    colors?.sale_badge_color,
    figmaColors.white,
  );
  const saleBadgeBgColor = propertyValue(
    colors?.sale_badge_bg_color,
    figmaDefaultColors.saleBadgeBgColor,
  );
  const addToCartBg = propertyValue(
    product?.add_to_cart_bg_color,
    primaryColor,
  );
  const addToCartColor = propertyValue(
    product?.add_to_cart_color,
    primaryInner,
  );
  const headerColor = propertyValue(header?.header_color, primaryInner);
  const headerBgColor = propertyValue(header?.header_bg_color, primaryColor);
  const headerIconColor = propertyValue(
    header?.header_icons_color,
    headerColor,
  );
  const headerCartbtnBgColor = propertyValue(
    header?.cart_button_bg_color,
    secondaryColor,
  );
  const headerCartbtnColor = propertyValue(
    header?.cart_button_color,
    secondaryInner,
  );
  const langSwitchBgColor = propertyValue(headerCartbtnBgColor, primaryColor);
  const langSwitchColor = propertyValue(headerCartbtnColor, primaryInner);
  return {
    primaryColor,
    primaryInner,
    secondaryColor,
    secondaryInner,
    floating,
    floatingInner,
    newBadgeColor,
    saleBadgeColor,
    newBadgeBgColor,
    saleBadgeBgColor,
    addToCartBg,
    addToCartColor,
    headerBgColor,
    headerColor,
    headerIconColor,
    headerCartbtnBgColor,
    headerCartbtnColor,
    langSwitchBgColor,
    langSwitchColor,
  };
};

export const colorSystem = (theme: ITheme) => {
  const { components } = theme ?? {};
  const { footer, body, product, tabs } = components ?? {};
  const {
    primaryColor,
    primaryInner,
    secondaryColor,
    secondaryInner,
    floating,
    floatingInner,
    newBadgeColor,
    saleBadgeColor,
    newBadgeBgColor,
    saleBadgeBgColor,
    addToCartBg,
    addToCartColor,
    headerBgColor,
    headerColor,
    headerIconColor,
    headerCartbtnBgColor,
    headerCartbtnColor,
    langSwitchBgColor,
    langSwitchColor,
  } = getColors(theme);

  //common
  setProperty("--primary-color", primaryColor);
  setProperty("--primary-inner-color", primaryInner);
  setProperty("--secondary-color", secondaryColor);
  setProperty("--secondary-inner-color", secondaryInner);
  setProperty("--floating-color", floating);
  setProperty("--floating-inner-color", floatingInner);
  setProperty("--new-badge-color", newBadgeColor);
  setProperty("--sale-badge-color", saleBadgeColor);
  setProperty("--new-badge-bg-color", newBadgeBgColor);
  setProperty("--sale-badge-bg-color", saleBadgeBgColor);
  //Header
  setProperty("--header-bg-color", headerBgColor);
  setProperty("--header-color", headerColor);
  setProperty("--header-icons-color", headerIconColor);
  setProperty("--header-cart-button-bg-color", headerCartbtnBgColor);
  setProperty("--header-cart-button-color", headerCartbtnColor);
  setProperty("--lang-switch-bg-color", langSwitchBgColor);
  setProperty("--lang-switch-color", langSwitchColor);

  //Footer
  setProperty("--footer-bg-color", footer?.bg_color, figmaColors.white);
  setProperty("--footer-color", footer?.color, figmaColors.dark);
  //Product
  const card_bg_color = propertyValue(
    product?.card_bg_color,
    figmaColors.white,
  );
  setProperty("--add-to-cart-bg-color", addToCartBg);
  setProperty("--add-to-cart-color", addToCartColor);
  setProperty("--product-card-bg-color", card_bg_color);
  setProperty(
    "--product-card-border-color",
    product?.card_border_color,
    card_bg_color,
  );
  setProperty("--product-name-color", product?.name_color, figmaColors.dark);
  setProperty("--product-price-color", product?.price_color, figmaColors.dark);
  //Body
  const input_font_color = propertyValue(body?.input_color, figmaColors.dark);
  const bodyBackgrounColor = propertyValue(body?.bg_color, figmaColors.gray6);
  document.body.style.backgroundColor = bodyBackgrounColor;
  setProperty("--body-bg-color", body?.bg_color, figmaColors.gray6);
  setProperty("--body-color", body?.color, figmaColors.dark);
  setProperty("--primary-with-opacity", hexToRGBA(primaryColor, 0.05));
  setProperty(
    "--section-heading-color",
    body?.section_heading_color,
    figmaColors.dark,
  );
  setProperty("--input-color", input_font_color);
  setProperty("--input-bg-color", body?.input_bg_color, "transparent");
  setProperty("--dropdown-bg-color", body?.input_bg_color, figmaColors.white);
  setProperty(
    "--input-plachoder-color",
    hexToRGBA(input_font_color, 0.5),
    figmaColors.dark,
  );
  setProperty("--btns-color", body?.btns_color, primaryInner);
  setProperty("--btns-bg-color", body?.btns_bg_color, primaryColor);
  //Tabs
  setProperty("--tabs-bg-color", tabs?.bg_color, figmaColors.gray6);
  setProperty("--tabs-color", tabs?.color, figmaColors.dark);
  //Font Families
  // setCustomFonts(language, body?.en_font_family, body?.ar_font_family);
};
export const useColorSystem = () => {
  const getCompany = useGetCompanyQuery();
  const theme = getCompany?.data?.result?.theme;
  return getColorSystem(theme!);
};
export const getColorSystem = (theme: ITheme) => {
  const allColors = {
    ...getColors(theme!),
    ...figmaColors,
  };
  return allColors;
};

export const setProductCarouselAdvanceSettings = (
  carouselAdvanceSettings: IProduct_Category_Config_Customize,
) => {
  setProperty(
    "--advance-carousel-product-width-mobile",
    `${carouselAdvanceSettings?.width_mobile}px`,
  );
  setProperty(
    "--advance-carousel-product-width",
    `${carouselAdvanceSettings?.width}px`,
  );
  setProperty(
    "--advance-carousel-product-height-mobile",
    `${carouselAdvanceSettings?.height_mobile}px`,
  );
  setProperty(
    "--advance-carousel-product-height",
    `${carouselAdvanceSettings?.height}px`,
  );
  //-------------------------------------

  setProperty(
    "--advance-carousel-product-img-width-mobile-sm",
    `${carouselAdvanceSettings?.width_mobile - 16}px`,
  );
  setProperty(
    "--advance-carousel-product-img-width-mobile-lg",
    `${carouselAdvanceSettings?.width_mobile - 32}px`,
  );
  setProperty(
    "--advance-carousel-product-img-width-sm",
    `${carouselAdvanceSettings?.width - 16}px`,
  );
  setProperty(
    "--advance-carousel-product-img-width-lg",
    `${carouselAdvanceSettings?.width - 32}px`,
  );
  setProperty(
    "--advance-carousel-product-img-height-mobile-sm",
    `${carouselAdvanceSettings?.height_mobile - 16}px`,
  );
  setProperty(
    "--advance-carousel-product-img-height-mobile-lg",
    `${carouselAdvanceSettings?.height_mobile - 32}px`,
  );
  setProperty(
    "--advance-carousel-product-img-height-sm",
    `${carouselAdvanceSettings?.height - 16}px`,
  );
  setProperty(
    "--advance-carousel-product-img-height-lg",
    `${carouselAdvanceSettings?.height - 32}px`,
  );
  //..........................................
  setProperty(
    "--advance-carousel-product-font-size-mobile",
    `${carouselAdvanceSettings?.font_size_mobile}px`,
  );
  setProperty(
    "--advance-carousel-product-font-size",
    `${carouselAdvanceSettings?.font_size_mobile}px`,
  );
  setProperty(
    "--advance-carousel-product-background-color",
    carouselAdvanceSettings?.background_color,
  );
};
